import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { Component, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { AppTextService } from '@fi-shared/app-text';
import { EditTabModel, EditTabsModel } from '@fi-app/utilities/edit-tabs/edit-tabs.model';
import { MindMapImport } from './mind-map-import.model';
import { AccessRightFilterSettings } from '@fi-shared/access-right-filters';
import { ModuleType } from '@fi-shared/types/module-type';
import { ILightWeightUser, PublicationSettingModel } from '@fi-shared/models';
import { DropdownSettingsModelV2 } from '@fi-shared/dropdown';
import { DocumentTemplateService } from '@fi-app/document-template/document-template.service';
import { propComparator } from '@fi-shared/utils/array-sort-comparators';
import { UserService } from '@fi-app/user/user.service';
import { FileUploadModel } from '@fi-app/utilities/file-upload/file-upload.model';
import { MindMapImportService } from './mind-map-import.service';
import { MediaBankFileModel } from '@fi-app/media-bank/media-bank-file.model';
import { AccessRightFiltersComponent } from '@fi-shared/access-right-filters/access-right-filters.component';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { IBackendErrorData } from '@fi-shared/fi-backend/fi-backend.service';
import { ActionFailureReasonCode } from '@fi-shared/types/action-failed-reason-code';
import { ILightWeightTemplate } from '@fi-app/document-template/document-template';
import { DetectDevicesService } from '@fi-shared/fi-shared-services';
import { INameIdObj } from '@fi-shared/fi-shared-interfaces';
import { FiFormatDateService } from '@fi-shared/fi-format-date/fi-format-date.service';
import { ObjectTypes } from '@fi-shared/types';

@Component({
    selector: 'fi-mind-map-import',
    templateUrl: './mind-map-import.component.html',
    styleUrls: ['./mind-map-import.component.scss'],
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-mind-map-import' },
    standalone: false,
})
export class MindMapImportComponent implements OnInit {
    @ViewChild(AccessRightFiltersComponent, { static: false }) accessRightFilters: AccessRightFiltersComponent;

    EditTabs: EditTabsModel;
    MindMapImport: MindMapImport = new MindMapImport();
    AccessRightFilterSettings: AccessRightFilterSettings;
    PublicationSetting: PublicationSettingModel = new PublicationSettingModel();
    templatesListData = new DropdownSettingsModelV2<ILightWeightTemplate>({
        filterBy: 'Name',
    });
    usersListData = new DropdownSettingsModelV2<INameIdObj>({
        filterBy: 'Name',
    });
    File: MediaBankFileModel;
    FileUploadModel: FileUploadModel;
    private readonly listDataFilterByPropertyName: string = 'Name';

    constructor(
        private fiDialogsService: FiDialogsService,
        private fiLoadingService: LoadingService,
        private stateService: FiStateService,
        private appTextService: AppTextService,
        private templateService: DocumentTemplateService,
        private userService: UserService,
        private mindMapImportService: MindMapImportService,
        private detectDevicesService: DetectDevicesService,
        private fiFormatDateService: FiFormatDateService,
    ) {}

    ngOnInit() {
        this.File = new MediaBankFileModel(this.fiFormatDateService);
        this.setPageHeaderData();
        this.setListsDataSettings();
        // var getModelData = () => {
        //     return {
        //         import: this.MindMapImport,
        //         publication: this.PublicationSetting
        //     };
        // }
        // this.saveChangesWarningService.Start(null, getModelData(), getModelData);
    }

    save() {
        this.PublicationSetting.ApplyToPublicationItem(this.MindMapImport);

        const createdByUserObject = this.usersListData.dataSet.find((user: INameIdObj) => {
            return user.Id === this.MindMapImport.CreatedByUserId;
        });

        this.MindMapImport.CreatedByUserName = createdByUserObject.Name;

        this.fiLoadingService.trackPromise(
            this.mindMapImportService.ImportMindMapFile(this.MindMapImport).then(
                () => {
                    this.fiDialogsService.notify({
                        messageText: this.appTextService.get('import.successful'),
                    });

                    this.accessRightFilters.OnAccessRightFilterCollectionSaved();
                    //this.saveChangesWarningService.stop();
                    this.stateService.go(FiStateNames.Document.List);
                },
                (err: IBackendErrorData) => {
                    const reason: ActionFailureReasonCode = err?.error?.Reason;
                    this.fiDialogsService.error({
                        messageText:
                            reason === ActionFailureReasonCode.Document_ImportHtmlError
                                ? err?.error?.Data?.ErrorMessage
                                : '',
                    });
                },
            ),
        );
    }

    isMobilePhone(): boolean {
        return this.detectDevicesService.isMobilePhone();
    }

    private setPageHeaderData() {
        this.EditTabs = new EditTabsModel(this.appTextService.get('import.mind_manager'));
        this.EditTabs.Tabs.push(
            new EditTabModel(
                this.appTextService.get('import.mind_manager'),
                this.appTextService.get('import.mind_manager'),
            ),
        );
    }

    private setListsDataSettings() {
        this.AccessRightFilterSettings = new AccessRightFilterSettings(
            ModuleType.Document,
            false,
            true,
            ObjectTypes.Document,
        );

        this.templateService.GetLightWeightTemplates().then((lightWeightTemplates: ILightWeightTemplate[]) => {
            const dataSet: ILightWeightTemplate[] = lightWeightTemplates
                .sort(propComparator(this.listDataFilterByPropertyName))
                .filter((item: ILightWeightTemplate) => item.HasViewAccess);
            this.templatesListData.setDataSet(dataSet);
        });

        this.userService.GetLightWeightUsers().then((lightWeightUsers: ILightWeightUser[]) => {
            let dataSet = lightWeightUsers
                .map((user) => ({
                    Id: user.Id,
                    Name: user.FullName,
                }))
                .sort(propComparator(this.listDataFilterByPropertyName));
            this.usersListData.setDataSet(dataSet);
        });

        this.FileUploadModel = new FileUploadModel(($files: FileList) => {
            this.File.PercentageLoaded = 0;

            if (!!$files.length) {
                const file = $files[0];
                this.File.Name = file.name;
                this.fiLoadingService
                    .trackPromise(this.mindMapImportService.ReadFileAsync(file))
                    .then((fileContent: string) => {
                        this.File.PercentageLoaded = 100;
                        this.File.PercentageProcessed = 100;
                        this.MindMapImport.FileContent = fileContent;
                    });
            }
        });
    }
}
